/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Dropdown, Row } from "antd";
import CoverSection, { SummaryRef as CoverRef } from "./Sections/Cover";
import SignatureSection, {
  SummaryRef as SignatureRef,
} from "./Sections/Signatures";
import SummarySection from "./Sections/Summary";
import PricingSection from "./Sections/Pricing";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { getProposalBuilderData } from "../ducks/actions";
import { saveProposalBuilder } from "../ducks/services";
import ScopeOfWork from "./Sections/ScopeOfWork";
import FullPageLayout from "../../../templates/FullPageLayout";
import DocumentHeader from "./DocumentHeader";
import { NeedHelp } from "../../../atoms/CustomIcons";
import { PlusOutlined } from "@ant-design/icons";
import Terms from "./Sections/Terms";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import dayjs from "dayjs";
import { POSITION_NAMES } from "../../../config/utils";
import { useNavigate } from "react-router-dom";

export default function DynamicSectionsEditor() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saveState, setSaveState] = useState(true);
  const [sections, setSections] = useState<
    { key: string; content: JSX.Element }[]
  >([]);

  const sectionRegistry: {
    [key: string]: {
      component: any;
      refKey: string;
      defaultProps: Record<string, any>;
    };
  } = {
    Cover: {
      component: CoverSection,
      refKey: "Cover",
      defaultProps: {
        sections: sections,
      },
    },
    Summary: {
      component: SummarySection,
      refKey: "Summary",
      defaultProps: {
        /* your default Signature props here */
      },
    },
    Pricing: {
      component: PricingSection,
      refKey: "Pricing",
      defaultProps: {
        /* your default Signature props here */
      },
    },
    Scope: {
      component: ScopeOfWork,
      refKey: "Scope",
      defaultProps: {
        /* your default Signature props here */
      },
    },
    Terms: {
      component: Terms,
      refKey: "Terms ",
      defaultProps: {
        /* your default Signature props here */
      },
    },
    Signatures: {
      component: SignatureSection,
      refKey: "Signatures",
      defaultProps: {
        /* your default Signature props here */
      },
    },
  };

  // Since all sections are now shown by default, sectionKeys will be empty
  // This is used for the dropdown to add new sections
  const sectionKeys = Object.keys(sectionRegistry).filter(
    (key) => !sections.some((s) => s.key === key)
  );

  const sectionRefs = useRef<Record<string, CoverRef | SignatureRef | null>>(
    {}
  );
  const locationUrl = window.location.pathname;
  const pathSegments = locationUrl.split("/");
  // const [isViewMode, setIsViewMode] = useState(false);
  const companyId = pathSegments[pathSegments.length - 1];
  const dealId = pathSegments[pathSegments.length - 2];
  const proposalId = pathSegments[pathSegments.length - 3];
  const proposalBuilderData = useAppSelector(
    (state) => state?.potentialDeal?.proposalBuilderData
  );
  const [proposalTitle, setProposalTitle] = useState<string>("");
  console.log("proposalBuilderData", proposalBuilderData?.data);

  useEffect(() => {
    if (proposalId) {
      const payload = {
        proposal_id: proposalId,
      };
      dispatch(getProposalBuilderData(payload));
    }
  }, [dispatch, proposalId]);

  // function extractInnerTextBySection(
  //   html: string,
  //   sectionName: string
  // ): string {
  //   try {
  //     const htmlParser = JSON.parse(html);
  //     const filteredHtml = htmlParser.find(
  //       (item: { section: string }) => item.section === sectionName
  //     );
  //     return (filteredHtml?.html || "").trim();
  //   } catch (e) {
  //     console.error(`Failed to extract text for section "${sectionName}":`, e);
  //     return "";
  //   }
  // }

  function extractInnerTextBySection(
    html: string,
    sectionName: string,
    className?: string
  ): string {
    try {
      const htmlParser = JSON.parse(html);
      const filteredHtml = htmlParser.find(
        (item: { section: string }) => item.section === sectionName
      );
      const rawHtml = (filteredHtml?.html || "").trim();

      if (!className) return rawHtml;

      // Parse the HTML and extract content from the specified class
      const cleanedHtml = rawHtml
        .replace(/\\n/g, "")
        .replace(/\\"/g, '"')
        .replace(/className=/g, "class"); // Normalize JSX to HTML

      const parser = new DOMParser();
      const doc = parser.parseFromString(cleanedHtml, "text/html");
      const el = doc.querySelector(`.${className}`);
      return el?.innerHTML?.trim() ?? "";
    } catch (e) {
      console.error(`Failed to extract text for section "${sectionName}":`, e);
      return "";
    }
  }

  const sectionLabels: Record<string, string> = {
    Cover: "Cover Page",
    Summary: "Executive Summary",
    Pricing: "Pricing Table",
    Scope: "Scope of Work",
    Terms: "Terms and Conditions",
    Signatures: "Signatures",
  };

  // Function to create all sections with their default content
  const createAllSections = (proposalData: any, htmlData: string) => {
    const allSections: { key: string; content: JSX.Element }[] = [];

    // Cover section (always first)
    allSections.push({
      key: "Cover",
      content: (
        <CoverSection
          ref={(ref) => (sectionRefs.current["Cover"] = ref)}
          company_name={proposalData?.company?.name ?? ""}
          date={proposalData?.date ?? ""}
          responsible_manager_name={
            proposalData?.responsible_manager?.first_name &&
            proposalData?.responsible_manager?.last_name
              ? `${proposalData.responsible_manager.first_name} ${proposalData.responsible_manager.last_name}`
              : ""
          }
          responsible_manager_phone={
            proposalData?.responsible_manager?.phone ?? ""
          }
          responsible_manager_email={
            proposalData?.responsible_manager?.email ?? ""
          }
          client_contact_name={
            proposalData?.proposal?.potential_deal?.property?.client?.name ?? ""
          }
          responsible_manager_title={
            proposalData?.responsible_manager?.title ?? ""
          }
          proposal_title={extractInnerTextBySection(
            htmlData,
            "Cover",
            "editableTitle"
          )}
          coverLetter={extractInnerTextBySection(
            htmlData,
            "Cover",
            "coverText"
          )}
          sections={Object.keys(sectionLabels).map((key) => ({
            key,
            label: sectionLabels[key],
          }))}
          proposalData={proposalData ?? {}}
          setProposalTitle={setProposalTitle}
          proposalTitle={proposalTitle}
          companyLogo={proposalData?.company?.image_url ?? ""}
          propertyName={
            proposalData?.proposal?.potential_deal?.property?.name ?? ""
          }
        />
      ),
    });

    // Summary section
    allSections.push({
      key: "Summary",
      content: (
        <SummarySection
          ref={(ref) => (sectionRefs.current["Summary"] = ref)}
          title="Summary"
          content={extractInnerTextBySection(htmlData, "Summary", "")}
          companyLogo={proposalData?.company?.image_url ?? ""}
          index={2}
        />
      ),
    });

    // Scope of Work section
    allSections.push({
      key: "Scope",
      content: (
        <ScopeOfWork
          ref={(ref) => (sectionRefs.current["Scope"] = ref)}
          content={extractInnerTextBySection(htmlData, "Scope", "")}
          index={3}
          companyLogo={proposalData?.company?.image_url ?? ""}
        />
      ),
    });

    // Pricing section
    allSections.push({
      key: "Pricing",
      content: (
        <PricingSection
          ref={(ref) => (sectionRefs.current["Pricing"] = ref)}
          index={4}
          companyLogo={proposalData?.company?.image_url ?? ""}
        />
      ),
    });

    // Terms section
    allSections.push({
      key: "Terms",
      content: (
        <Terms
          ref={(ref) => (sectionRefs.current["Terms"] = ref)}
          content={extractInnerTextBySection(htmlData, "Terms", "")}
          index={5}
          companyLogo={proposalData?.company?.image_url ?? ""}
        />
      ),
    });

    // Signatures section (always last)
    allSections.push({
      key: "Signatures",
      content: (
        <SignatureSection
          ref={(ref) => (sectionRefs.current["Signatures"] = ref)}
          company_name={proposalData?.company?.name}
          date={proposalData?.date ?? dayjs().format("YYYY-MM-DD")}
          responsible_manager_name={`${
            proposalData?.responsible_manager?.first_name ?? ""
          } ${proposalData?.responsible_manager?.last_name ?? ""}`}
          company_website={proposalData?.company?.company_website}
          client_name={
            proposalData?.proposal?.potential_deal?.property?.client?.name ?? ""
          }
          responsible_manager_title="Project Manager"
          client_contact_name={`${proposalData?.proposal?.potential_deal?.property?.client?.clientContacts?.[0]?.first_name} ${proposalData?.proposal?.potential_deal?.property?.client?.clientContacts?.[0]?.last_name}`}
          client_contact_title={
            proposalData?.proposal?.potential_deal?.property?.client
              ?.clientContacts?.[0]?.title ?? ""
          }
          index={6}
          companyLogo={proposalData?.company?.image_url ?? ""}
        />
      ),
    });

    return allSections;
  };

  useEffect(() => {
    if (!proposalBuilderData?.data) return;

    const htmlData = proposalBuilderData.data.html ?? "[]";
    console.log("Creating all sections with saved data...");

    // Create all sections (they will show empty content if no saved data exists)
    const allSections = createAllSections(proposalBuilderData.data, htmlData);

    setSections(allSections);
    setProposalTitle(
      proposalBuilderData?.data?.proposal?.potential_deal?.name ?? ""
    );
  }, [proposalBuilderData?.data]);

  useEffect(() => {
    const visibleSectionLabels = sections.map((sec) => ({
      key: sec.key,
      label: sectionLabels[sec.key] ?? sec.key,
    }));

    const updatedSections = sections.map((sec) => {
      console.log("3");
      if (sec.key === "Cover") {
        return {
          ...sec,
          content: (
            <CoverSection
              ref={(ref) => (sectionRefs.current["Cover"] = ref)}
              company_name={proposalBuilderData?.data?.company?.name ?? ""}
              date={proposalBuilderData?.data?.date ?? ""}
              responsible_manager_name={
                proposalBuilderData?.data?.responsible_manager?.first_name &&
                proposalBuilderData?.data?.responsible_manager?.last_name
                  ? `${proposalBuilderData.data.responsible_manager.first_name} ${proposalBuilderData.data.responsible_manager.last_name}`
                  : ""
              }
              responsible_manager_phone={
                proposalBuilderData?.data?.responsible_manager?.phone ?? ""
              }
              responsible_manager_email={
                proposalBuilderData?.data?.responsible_manager?.email ?? ""
              }
              client_contact_name={
                proposalBuilderData?.data?.proposal?.potential_deal?.property
                  ?.client?.name ?? ""
              }
              responsible_manager_title={
                proposalBuilderData?.data?.responsible_manager?.title ?? ""
              }
              proposal_title={extractInnerTextBySection(
                proposalBuilderData?.data?.html ?? "[]",
                "Cover",
                "editableTitle"
              )}
              coverLetter={extractInnerTextBySection(
                proposalBuilderData?.data?.html ?? "[]",
                "Cover",
                "coverText"
              )}
              sections={visibleSectionLabels} // ✅ this updates!
              proposalData={
                proposalBuilderData?.data ? proposalBuilderData?.data : {}
              }
              setProposalTitle={setProposalTitle}
              proposalTitle={proposalTitle}
              companyLogo={proposalBuilderData?.data?.company?.image_url ?? ""}
              propertyName={
                proposalBuilderData?.data?.proposal?.potential_deal?.property
                  ?.name ?? ""
              }
            />
          ),
        };
      }

      return sec;
    });

    setSections(updatedSections);
  }, [sections.length]);

  const handleSave = () => {
    if (proposalBuilderData?.data && proposalBuilderData?.data?.html) {
      setLoading(true);
    }
    const rawHtml = sections
      .map(({ key }) => {
        const ref = sectionRefs.current[key];
        return ref?.getFullHtml?.() || "";
      })
      .join("");

    const result = sections.map(({ key }) => {
      const ref = sectionRefs.current[key];
      const html = ref?.getHtml?.() || "";

      return { section: key, html };
    });

    const visibleSectionLabels = sections.map(
      (sec) => sectionLabels[sec.key] ?? sec.key
    );

    const payload = {
      proposal_id: proposalId,
      deal_id: dealId || "",
      company_id: companyId || "",
      document: JSON.stringify(result),
      complete_html: rawHtml,
      proposal_name: proposalTitle,
      table_columns: visibleSectionLabels,
    };
    saveProposalBuilder(payload)
      .then(() => {
        setLoading(false);
        const payload = {
          proposal_id: proposalId,
        };
        dispatch(getProposalBuilderData(payload));
        if (proposalBuilderData?.data && proposalBuilderData?.data?.html) {
          navigate(`/deals-detail/${dealId}/${companyId}`);
          SuccessMessage("Document saved");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (
      proposalBuilderData.data &&
      !proposalBuilderData.data.html &&
      sections.length > 0 &&
      saveState
    ) {
      setSaveState(false);
      setTimeout(() => {
        handleSave();
      }, 2500);
    }
  }, [proposalBuilderData?.data, sections, saveState]);

  console.log("sections", proposalBuilderData?.data?.html);

  return (
    <FullPageLayout loading={proposalBuilderData?.loader || loading}>
      <div className="estimateWrapper">
        <DocumentHeader
          companyId={companyId}
          dealId={dealId}
          proposalId={proposalId}
          btnText1="Need help?"
          btnIcon1={<NeedHelp />}
          setLoading={setLoading}
          proposalBuilderData={proposalBuilderData?.data}
          addProposal={true}
          handleSubmitEstimate={handleSave}
        />

        <Card
          className="editorContent editorContentTemplate"
          style={{ margin: 0 }}
        >
          <Row gutter={12} wrap={false}>
            <Col flex="234px">
              <Card
                style={{
                  backgroundColor: "#F6F9F9",
                  border: "unset",
                }}
              >
                <div className="editorHeader">
                  <h3> Sections </h3>

                  {/* <Dropdown.Button
                    overlay={sectionMenu}
                    style={{
                      backgroundColor: "#fff",
                      height: "28px",
                      width: "28px",
                    }}
                  >
                    <PlusOutlined />
                  </Dropdown.Button> */}

                  {sectionKeys?.length > 0 && (
                    <Dropdown
                      menu={{
                        items: sectionKeys?.map((key) => ({
                          key,
                          label: (
                            <div style={{ padding: "8px 12px" }}>{key}</div>
                          ),
                          onClick: () => {
                            const {
                              component: SectionComp,
                              refKey,
                              defaultProps,
                            } = sectionRegistry[key];

                            setSections((prev) => {
                              // Filter out Cover and Signatures to count only indexable sections
                              const contentSections = prev.filter(
                                (s) =>
                                  s.key !== "Cover" && s.key !== "Signatures"
                              );

                              const newSectionIndex =
                                contentSections.length + 2;

                              const newSection = {
                                key,
                                content: (
                                  <SectionComp
                                    ref={(ref: any) =>
                                      (sectionRefs.current[refKey] = ref)
                                    }
                                    index={newSectionIndex}
                                    companyLogo={
                                      proposalBuilderData?.data?.company
                                        ?.image_url
                                    }
                                    {...defaultProps}
                                  />
                                ),
                              };

                              const withoutSignature = prev.filter(
                                (s) => s.key !== "Signatures"
                              );
                              const signatureSection = prev.find(
                                (s) => s.key === "Signatures"
                              );

                              const updated = [...withoutSignature, newSection];

                              // ✅ If signature exists, re-clone with updated index and props
                              if (signatureSection) {
                                const SignatureComp =
                                  sectionRegistry["Signatures"].component;
                                const signatureRefKey =
                                  sectionRegistry["Signatures"].refKey;

                                const signatureIndex =
                                  updated.filter(
                                    (s) =>
                                      s.key !== "Cover" &&
                                      s.key !== "Signatures"
                                  ).length + 2;

                                updated.push({
                                  key: "Signatures",
                                  content: (
                                    <SignatureComp
                                      ref={(ref: any) =>
                                        (sectionRefs.current[signatureRefKey] =
                                          ref)
                                      }
                                      index={signatureIndex}
                                      company_name={
                                        proposalBuilderData?.data?.company?.name
                                      }
                                      date={
                                        proposalBuilderData?.data?.date ??
                                        dayjs().format("YYYY-MM-DD")
                                      }
                                      responsible_manager_name={`${
                                        proposalBuilderData?.data
                                          ?.responsible_manager?.first_name ??
                                        ""
                                      } ${
                                        proposalBuilderData?.data
                                          ?.responsible_manager?.last_name ?? ""
                                      }`}
                                      company_website={
                                        proposalBuilderData?.data?.company
                                          ?.company_website
                                      }
                                      client_name={
                                        proposalBuilderData?.data?.proposal
                                          ?.potential_deal?.property?.client
                                          ?.name
                                      }
                                      responsible_manager_title={
                                        proposalBuilderData?.data
                                          ?.responsible_manager?.position_id
                                          ? POSITION_NAMES[
                                              proposalBuilderData?.data
                                                ?.responsible_manager
                                                ?.position_id
                                            ]
                                          : ""
                                      }
                                      client_contact_name={`${
                                        proposalBuilderData?.data?.proposal
                                          ?.potential_deal?.property?.client
                                          ?.clientContacts?.[0]?.first_name ??
                                        ""
                                      } ${
                                        proposalBuilderData?.data?.proposal
                                          ?.potential_deal?.property?.client
                                          ?.clientContacts?.[0]?.last_name ?? ""
                                      }`}
                                      client_contact_title={
                                        proposalBuilderData?.data?.proposal
                                          ?.potential_deal?.property?.client
                                          ?.clientContacts?.[0]?.title ?? ""
                                      }
                                      companyLogo={
                                        proposalBuilderData?.data?.company
                                          ?.image_url ?? ""
                                      }
                                    />
                                  ),
                                });
                              }

                              return updated;
                            });
                          },
                        })),
                      }}
                      trigger={["click"]}
                      placement="bottomRight"
                      className="actionButton fff"
                    >
                      <Button
                        onClick={(x) => {
                          x.stopPropagation();
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{
                          backgroundColor: "#fff",
                          height: "28px",
                          width: "28px",
                          borderRadius: "8px",
                          border: "1px solid #DEE5E6",
                        }}
                        className="only_icon"
                        icon={<PlusOutlined />}
                      />
                    </Dropdown>
                  )}
                </div>
                <div className="editorBody">
                  <ul>
                    {sections.map((s, idx) => (
                      <li key={idx}>
                        {idx + 1}. {s.key}
                      </li>
                    ))}
                  </ul>
                  {/* <ul>
                    <li>Cover</li>
                    <li>
                      <DragIcon style={{ marginRight: "8px" }} /> Summary
                    </li>
                    <li>
                      <DragIcon style={{ marginRight: "8px" }} />
                      Scope of work
                    </li>
                    <li>
                      <DragIcon style={{ marginRight: "8px" }} />
                      Pricing
                    </li>
                    <li>
                      <DragIcon style={{ marginRight: "8px" }} />
                      Terms & conditions
                    </li>
                    <li>Signatures</li>
                  </ul> */}
                </div>
              </Card>
            </Col>
            <Col flex="auto">
              {/* <div className="document-toolbar">
                <Image
                  src="/images/potential-deals/toolbar.svg"
                  preview={false}
                />
              </div> */}
              <Card
                className="documentEditor additional_card_ui"
                style={{
                  margin: 0,
                  backgroundColor: "#DBF1EA",
                  border: "unset",
                }}
              >
                <div className="documentEditorMain">
                  {sections.map((section) => (
                    <Fragment key={section.key}>{section.content}</Fragment>
                  ))}
                </div>
                {/* 
                {(proposalBuilderData?.data?.proposal?.status === "accepted" ||
                  proposalBuilderData?.data?.proposal?.status ===
                    "sent_to_production") && (
                  <div className="overlayForSendProduction"></div>
                )} */}
              </Card>
            </Col>
          </Row>
        </Card>
      </div>
    </FullPageLayout>
  );
}
